import React from 'react';
import { Flex } from '@withjoy/joykit';
import type { CSSProperties, FC } from 'react';
import { Children, useLayoutEffect, useRef } from 'react';
import { useSurface } from '../steps/CardDesign/useSurface';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';

// Roles:
// 1) Parent controls width.
// 2) Child controls aspect ratio (via height/width).
//    The Parent should follow with height auto.
// 3) This controls the scale and sets it on the child.
type PageScalerProps = Pick<CSSProperties, 'position'> & {
  /**
   * Fires after each measure-and-scale pass, once the child carries its final geometry.
   * Callers that must not paint the un-scaled child (guest monogram keeps it
   * visibility:hidden until sized, so the resize can't score as a layout shift)
   * use this as the reveal signal. ResizeObserver guarantees an initial delivery
   * on observe, so this always fires at least once.
   */
  onScale?: (scale: number) => void;
};

export const PageScaler: FC<PageScalerProps> = props => {
  const { children, onScale, ...cssProps } = props;
  const [, update] = useSurface();
  const ref = useRef<HTMLDivElement>(null);
  // Latest-callback ref so a new onScale identity doesn't tear down the observer.
  const onScaleRef = useRef(onScale);
  onScaleRef.current = onScale;

  useLayoutEffect(() => {
    const self = ref.current;
    if (!self) return;

    const resizeObserver = createSafeResizeObserver(
      () => {
        const child = self.firstElementChild as HTMLElement;
        if (!child) return;

        const scale = self.clientWidth / child.clientWidth;
        self.style.height = `${child.clientHeight * scale}px`;
        child.style.position = 'absolute';
        child.style.scale = `${scale}`;
        update(draft => {
          draft.scale = scale;
        });
        onScaleRef.current?.(scale);
      },
      { skipRAF: true },
    );

    // The scale is a function of self and child, so observing both.
    resizeObserver.observe(self);
    if (self.firstElementChild) {
      // In Envelope, the firstElementChild clientWidth does not immediately reflect the style width.
      // Thus observing firstElementChild so we update when clientWidth updates.
      resizeObserver.observe(self.firstElementChild);
    }
    return () => resizeObserver.disconnect();
  }, [update, ref]);

  return (
    <Flex
      ref={ref}
      position="relative"
      width="100%" // Match parent width by convention.
      {...cssProps} // Allows caller to override position relative.
      alignItems="center"
      justifyContent="center"
    >
      {Children.only(children)}
    </Flex>
  );
};
